import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import type { Demanda } from '../../../models';
import { useEventListener } from '../../../hooks/useEventBus';
import { useDemandNavigation } from '../hooks/useDemandNavigation';
import { useDemandSelector } from '../hooks/useDemandSelector';
import { useDemandQuery } from '../hooks/queries/useDemandQuery';

export const useDemandViewLogic = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Tipo de demanda para filtrar a lista (received, sent, watching)
  const demandType = searchParams.get('type') || 'all';

  // Usar TanStack Query para buscar a demanda
  const {
    data: demanda,
    isLoading: loading,
    error: queryError,
    refetch: refetchDemanda,
  } = useDemandQuery(id ? Number(id) : undefined);

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Erro ao carregar demanda') : null;

  // Hook para seleção local de demandas (sem recarregar página)
  const demandSelector = useDemandSelector({ initialDemanda: demanda || null });

  // Hook de navegação de demandas
  const navigation = useDemandNavigation({ 
    currentDemanda: demandSelector.selectedDemanda || demanda || null, 
    demandType,
    onDemandSelect: (selectedDemanda: Demanda) => {
      // Usar seleção local ao invés de navegação
      demandSelector.selectDemand(selectedDemanda);
    }
  });

  const navigateToNextDemand = () => {
    const currentIndex = navigation.allDemandas.findIndex(d => d.id === demanda?.id);
    if (currentIndex >= 0 && currentIndex < navigation.allDemandas.length - 1) {
      const nextDemanda = navigation.allDemandas[currentIndex + 1];
      navigate(`/demands/${nextDemanda.id}/view?type=all`);
    }
  };

  const navigateToPreviousDemand = () => {
    const currentIndex = navigation.allDemandas.findIndex(d => d.id === demanda?.id);
    if (currentIndex > 0) {
      const previousDemanda = navigation.allDemandas[currentIndex - 1];
      navigate(`/demands/${previousDemanda.id}/view?type=all`);
    }
  };

  const goBackToList = () => {
    switch (demandType) {
      case 'sent':
        navigate('/demands/sent');
        break;
      case 'watching':
        navigate('/demands/watching');
        break;
      default:
        navigate('/demands/received');
    }
  };

  const currentIndex = navigation.allDemandas.findIndex(d => d.id === (demandSelector.selectedDemanda || demanda)?.id);
  const canNavigateNext = currentIndex >= 0 && currentIndex < navigation.allDemandas.length - 1;
  const canNavigatePrevious = currentIndex > 0;

  // Função para recarregar a demanda após edição (agora usa refetch do TanStack Query)
  const refreshDemand = async () => {
    if (id) {
      await refetchDemanda();
    }
  };

  // Escuta eventos de atualização - TanStack Query refetch automático quando cache é invalidado
  // Comparar com a demanda atualmente visualizada (selectedDemanda ou demanda da URL)
  const currentDemandaId = (demandSelector.selectedDemanda || demanda)?.id;

  useEventListener('demand-updated', async ({ demandId }) => {
    if (demandId === currentDemandaId) {
      // Se estamos vendo uma demanda selecionada (diferente da URL), atualizar via selector
      if (demandSelector.selectedDemanda && demandSelector.selectedDemanda.id !== Number(id)) {
        await demandSelector.refreshSelectedDemand();
      } else {
        await refetchDemanda();
      }
    }
  }, [currentDemandaId, refetchDemanda, demandSelector, id]);

  useEventListener('demand-status-changed', async ({ demandId }) => {
    if (demandId === currentDemandaId) {
      // Se estamos vendo uma demanda selecionada (diferente da URL), atualizar via selector
      if (demandSelector.selectedDemanda && demandSelector.selectedDemanda.id !== Number(id)) {
        await demandSelector.refreshSelectedDemand();
      } else {
        await refetchDemanda();
      }
    }
  }, [currentDemandaId, refetchDemanda, demandSelector, id]);

  useEventListener('demand-assigned', async ({ demandId }) => {
    if (demandId === currentDemandaId) {
      // Se estamos vendo uma demanda selecionada (diferente da URL), atualizar via selector
      if (demandSelector.selectedDemanda && demandSelector.selectedDemanda.id !== Number(id)) {
        await demandSelector.refreshSelectedDemand();
      } else {
        await refetchDemanda();
      }
    }
  }, [currentDemandaId, refetchDemanda, demandSelector, id]);

  return {
    // Demanda principal (inicial da URL) ou demanda selecionada localmente
    demanda: demandSelector.selectedDemanda || demanda || null,
    loading: loading || demandSelector.loading,
    error: error || demandSelector.error,
    currentIndex,
    totalDemandas: navigation.allDemandas.length,
    canNavigateNext,
    canNavigatePrevious,
    navigateToNextDemand,
    navigateToPreviousDemand,
    goBackToList,
    demandType,
    refreshDemand,
    // Smart refresh (simplificado com TanStack Query)
    isRefreshing: loading, // TanStack Query já gerencia o loading do refetch
    smartRefresh: refreshDemand, // Usa o refetch do TanStack Query
    // Navegação de demandas relacionadas
    navigation: {
      allDemandas: navigation.allDemandas,
      relatedDemandas: navigation.relatedDemandas,
      loadingAllDemandas: navigation.loadingAllDemandas,
      selectDemandLocally: navigation.selectDemandLocally,
      navigateToDemand: navigation.navigateToDemand,
      viewDemandInNewTab: navigation.viewDemandInNewTab,
      refreshRelatedDemandas: navigation.refreshRelatedDemandas,
      selectedDemandId: navigation.selectedDemandId,
      listType: navigation.listType,
      setListType: navigation.setListType,
      availableListTypes: navigation.availableListTypes,
    },
    // Seletor de demandas
    demandSelector: {
      selectDemand: demandSelector.selectDemand,
      selectDemandById: demandSelector.selectDemandById,
      clearSelection: demandSelector.clearSelection,
      refreshSelectedDemand: demandSelector.refreshSelectedDemand,
    },
  };
};